import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Receipt,
  FileDown,
  Printer,
  Building2,
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  CheckCircle2,
  Sparkles
} from "lucide-react";
import { generateInvoicePdf } from "@/utils/generateInvoicePdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvoicePage({ businessInfo }) {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBooking = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${API}/bookings/${id}`);
        setBooking(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    loadBooking();
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-pink-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="bg-white p-12 rounded-3xl border border-pink-100 text-center space-y-4">
        <h2 className="text-xl font-bold text-pink-950">Invoice Not Found</h2>
        <Link
          to="/bookings"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-pink-900 text-white text-xs font-bold"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Bookings</span>
        </Link>
      </div>
    );
  }

  const hallName = businessInfo?.business_name || "Grand Royal Palace & Convention Hall";
  const hallPhone = businessInfo?.phone || "+91 98450 12345";
  const hallEmail = businessInfo?.email || "bookings@grandroyalpalace.com";
  const hallAddress = businessInfo?.address || "45 Heritage Boulevard, Bengaluru 560068";

  const hallRent = booking.hall_rent ?? booking.hallRent ?? booking.rent_amount ?? (booking.totalAmount || booking.total_amount || 0);
  const labor = Number(booking.labor_charges ?? booking.laborCharges ?? 0);
  const electricity = Number(booking.electricity_charges ?? booking.electricityCharges ?? 0);
  const maintenance = Number(booking.maintenance_fee ?? booking.maintenanceFee ?? 0);
  const applyGst = Boolean(booking.apply_gst || booking.applyGst || booking.sgst_amount > 0 || booking.cgst_amount > 0);
  const sgst = Number(booking.sgst_amount ?? booking.sgstAmount ?? (applyGst ? hallRent * 0.09 : 0));
  const cgst = Number(booking.cgst_amount ?? booking.cgstAmount ?? (applyGst ? hallRent * 0.09 : 0));
  const additionalCharges = booking.additional_charges || [];
  const total = Number(booking.totalAmount ?? booking.total_rent ?? booking.total_amount ?? booking.rent_amount ?? 0);
  const advance = Number(booking.advancePaid ?? booking.deposit_amount ?? booking.advance_paid ?? booking.advance_amount ?? 0);
  const balance = booking.balanceDue !== undefined ? Number(booking.balanceDue) : (booking.balance_due !== undefined ? Number(booking.balance_due) : Math.max(0, total - advance));

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      {/* Top Header with Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to={`/bookings/${id}`}
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-pink-900 hover:text-pink-950 bg-white px-4 py-2 rounded-xl border border-pink-200 shadow-sm transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Booking Details</span>
        </Link>

        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="px-4 py-2 rounded-xl bg-white hover:bg-pink-50 text-pink-900 border border-pink-200 text-xs font-bold shadow-sm flex items-center gap-1.5 transition-colors"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print Invoice</span>
          </button>
          <button
            data-testid="btn-download-modal-invoice"
            onClick={() => generateInvoicePdf(booking, businessInfo)}
            className="px-5 py-2 rounded-xl bg-pink-900 hover:bg-pink-950 text-white text-xs font-bold shadow flex items-center gap-1.5 transition-colors"
          >
            <FileDown className="w-3.5 h-3.5" />
            <span>Download PDF</span>
          </button>
        </div>
      </div>

      {/* Invoice Document Card */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-2xl overflow-hidden p-8 sm:p-12 space-y-8">
        
        {/* Invoice Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-pink-100 pb-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-pink-900 text-white flex items-center justify-center font-bold text-xs">
                BD
              </div>
              <span className="text-xl font-extrabold text-pink-950">BookMyEvents</span>
            </div>
            <p className="text-xs uppercase tracking-wider font-semibold text-pink-700">Official Tax Invoice & Receipt</p>
          </div>

          <div className="text-left sm:text-right space-y-0.5">
            <span className="text-sm font-bold text-gray-900 font-mono block">INVOICE #: BME-{(booking.id || "").slice(0, 8).toUpperCase()}</span>
            <span className="text-xs text-gray-500 block">Date: {new Date().toLocaleDateString("en-IN")}</span>
            <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full inline-block">
              {booking.status?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Venue & Customer 2-Column Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-xs">
          <div className="p-5 rounded-2xl bg-pink-50/50 border border-pink-100 space-y-2">
            <span className="font-bold text-pink-900 uppercase tracking-wide block">Venue / Issuer Details</span>
            <div className="font-bold text-gray-900 text-sm">{hallName}</div>
            <div className="text-gray-600">Phone: {hallPhone}</div>
            <div className="text-gray-600">Email: {hallEmail}</div>
            <div className="text-gray-600">Address: {hallAddress}</div>
          </div>

          <div className="p-5 rounded-2xl bg-pink-50/50 border border-pink-100 space-y-2">
            <span className="font-bold text-pink-900 uppercase tracking-wide block">Customer & Event Details</span>
            <div className="font-bold text-gray-900 text-sm">{booking.client_name}</div>
            <div className="text-gray-600">Phone: {booking.client_phone}</div>
            <div className="text-gray-600">Event: {booking.event_type}</div>
            <div className="text-gray-600">
              Dates: {booking.start_date === booking.end_date ? booking.start_date : `${booking.start_date} → ${booking.end_date}`}
            </div>
            <div className="text-gray-600 font-mono">Hours: {booking.start_time} - {booking.end_time} ({booking.slot_type?.toUpperCase()} SLOT)</div>
          </div>
        </div>

        {/* Itemized Table */}
        <div className="overflow-hidden rounded-2xl border border-pink-100">
          <table className="w-full text-left text-xs">
            <thead className="bg-pink-900 text-white font-bold">
              <tr>
                <th className="py-3 px-4">#</th>
                <th className="py-3 px-4">Charge Description</th>
                <th className="py-3 px-4">Type / Basis</th>
                <th className="py-3 px-4 text-right">Amount (INR)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pink-50 text-gray-700">
              <tr>
                <td className="py-3 px-4 text-gray-400">1</td>
                <td className="py-3 px-4 font-bold text-gray-900">Convention Hall Rent (Base Venue)</td>
                <td className="py-3 px-4 text-gray-500">1 Slot / Duration</td>
                <td className="py-3 px-4 text-right font-bold font-mono">₹{Number(hallRent).toLocaleString("en-IN")}</td>
              </tr>
              {labor > 0 && (
                <tr>
                  <td className="py-3 px-4 text-gray-400">2</td>
                  <td className="py-3 px-4 font-bold text-gray-900">Labor Charges (Staff & Cleaning)</td>
                  <td className="py-3 px-4 text-gray-500">Fixed Fee</td>
                  <td className="py-3 px-4 text-right font-bold font-mono">₹{Number(labor).toLocaleString("en-IN")}</td>
                </tr>
              )}
              {electricity > 0 && (
                <tr>
                  <td className="py-3 px-4 text-gray-400">3</td>
                  <td className="py-3 px-4 font-bold text-gray-900">Electricity & Utility Charges</td>
                  <td className="py-3 px-4 text-gray-500">Standard Consumption</td>
                  <td className="py-3 px-4 text-right font-bold font-mono">₹{Number(electricity).toLocaleString("en-IN")}</td>
                </tr>
              )}
              {maintenance > 0 && (
                <tr>
                  <td className="py-3 px-4 text-gray-400">4</td>
                  <td className="py-3 px-4 font-bold text-gray-900">Maintenance & Sanitation Fee</td>
                  <td className="py-3 px-4 text-gray-500">Standard Upkeep</td>
                  <td className="py-3 px-4 text-right font-bold font-mono">₹{Number(maintenance).toLocaleString("en-IN")}</td>
                </tr>
              )}
              {additionalCharges.map((ac, idx) => (
                <tr key={idx}>
                  <td className="py-3 px-4 text-gray-400">{5 + idx}</td>
                  <td className="py-3 px-4 font-bold text-gray-900">{ac.name || `Additional Charge #${idx + 1}`}</td>
                  <td className="py-3 px-4 text-gray-500">Optional Add-on</td>
                  <td className="py-3 px-4 text-right font-bold font-mono">₹{Number(ac.amount || 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
              {applyGst && (
                <>
                  <tr className="bg-pink-50/40">
                    <td className="py-3 px-4 text-pink-700 font-bold">*</td>
                    <td className="py-3 px-4 font-bold text-pink-950">SGST (9% on Hall Rent)</td>
                    <td className="py-3 px-4 text-pink-700">State GST (9%)</td>
                    <td className="py-3 px-4 text-right font-bold font-mono text-pink-950">₹{Number(sgst).toLocaleString("en-IN")}</td>
                  </tr>
                  <tr className="bg-pink-50/40">
                    <td className="py-3 px-4 text-pink-700 font-bold">*</td>
                    <td className="py-3 px-4 font-bold text-pink-950">CGST (9% on Hall Rent)</td>
                    <td className="py-3 px-4 text-pink-700">Central GST (9%)</td>
                    <td className="py-3 px-4 text-right font-bold font-mono text-pink-950">₹{Number(cgst).toLocaleString("en-IN")}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Invoice Summary */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4 border-t border-pink-100">
          <div className="text-xs text-gray-500 space-y-1 max-w-sm">
            <span className="font-bold text-gray-700 block">Payment Policies & Notes:</span>
            <p>1. Advance amount confirmed upon booking receipt.</p>
            <p>2. Balance due is payable prior to function commencement.</p>
          </div>

          <div className="w-full sm:w-72 p-5 rounded-2xl bg-pink-50 border border-pink-200 space-y-2 text-xs">
            <div className="flex justify-between text-gray-700 font-semibold">
              <span>Total Amount:</span>
              <span className="font-bold font-mono text-sm">₹{Number(total).toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between text-emerald-800 font-semibold">
              <span>Advance Paid:</span>
              <span className="font-bold font-mono text-sm">₹{Number(advance).toLocaleString("en-IN")}</span>
            </div>
            <div className="pt-2 border-t border-pink-200 flex justify-between text-pink-950 font-black text-sm">
              <span>Balance Due:</span>
              <span className="font-mono text-base text-rose-900">₹{Number(balance).toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
